<template>
  <div class="p-field">
    <div class="p-field-horizontal">
      <div class="p-checkbox-container" :class="{
        'p-invalid': isInvalid,
        'p-disabled': disabled
      }">
        <div class="p-checkbox-wrapper flex items-center gap-2">
          <div class="p-checkbox-box-wrapper ">
            <Checkbox ref="inputRef" :id="id" :modelValue="localValue" :value="value" :class="[
              'mop-checkbox',
              {
                'p-checkbox-sm': size === 'small',
                'p-checkbox-lg': size === 'large',
                'p-invalid': isInvalid,
                'p-disabled': disabled
              }
            ]" :disabled="disabled" :readonly="readonly" :required="required" :name="name"
              :aria-label="props['aria-label']" :aria-labelledby="props['aria-labelledby']" :aria-invalid="isInvalid"
              :aria-required="required" :aria-describedby="ariaDescribedBy" v-bind="$attrs"
              @update:modelValue="handleModelValueUpdate" @input="onInput" @change="onChange" @focus="onFocus"
              @blur="onBlur" />
          </div>
          <label v-if="label" :for="id" class="checkbox-label-text mt-1">
            {{ label }}
            <span v-if="required" class="text-red-500">*</span>
          </label>
          <span v-else-if="required" class="text-red-500">*</span>
        </div>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
      <small v-if="isInvalid" :id="`${id}-error`" class="p-error block text-red-500">
        {{ getFormattedErrorMessage }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Checkbox from 'primevue/checkbox'

export default defineComponent({
  name: 'MOPCheckbox',
  components: {
    Checkbox
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import type { MOPCheckboxProps } from './types'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'
import { useVeeValidate } from '../../../../composables/useVeeValidate'

const props = withDefaults(defineProps<MOPCheckboxProps>(), {
  modelValue: null,
  value: undefined,
  size: undefined,
  invalid: false,
  disabled: false,
  readonly: false,
  required: false,
  placeholder: '',
  helperText: '',
  label: '',
  'aria-label': '',
  'aria-labelledby': '',
  id: () => `mop-checkbox-${Math.random().toString(36).substr(2, 9)}`,
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  validateOnChange: true,
  showErrors: true,
  name: undefined,
  formContext: true
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean | unknown[] | null): void
  (e: 'input', event: Event): void
  (e: 'change', event: Event): void
  (e: 'focus', event: Event): void
  (e: 'blur', event: Event): void
  (e: 'validation-error', errors: string[]): void
  (e: 'validation-success'): void
}>()

// Form Context
const { formState } = useFormContext()
const { t } = useLocalization()

// Local State
const inputRef = ref<HTMLInputElement | null>(null)
const localValidationErrors = ref<string[]>([])
const isDirty = ref(false)
const isValidating = ref(false)
const touched = ref(false)

// Setup VeeValidate
const {
  fieldValue,
  errorMessage,
  shouldShowError,
  validate: veeValidate,
  handleValidation,
  updateValue,
  isEmpty
} = useVeeValidate({
  name: props.name || '',
  modelValue: Array.isArray(props.modelValue)
    ? props.modelValue.includes(props.value)
    : props.modelValue ?? false,
  required: props.required,
  customRules: props.customRules
})

const handleModelValueUpdate = (newValue: boolean) => {
  if (Array.isArray(props.modelValue)) {
    // Create a copy of the current array
    const currentArray = [...props.modelValue];

    if (newValue) {
      // Add the value to the array if it's not already there
      if (!currentArray.includes(props.value)) {
        currentArray.push(props.value);
      }
    } else {
      // Remove the value from the array
      const index = currentArray.indexOf(props.value);
      if (index !== -1) {
        currentArray.splice(index, 1);
      }
    }

    emit('update:modelValue', currentArray);
  } else {
    emit('update:modelValue', newValue);
  }

  if (props.formContext && formState && props.name) {
    formState.setFieldValue(props.name, Array.isArray(props.modelValue) ?
      (newValue ? [...props.modelValue, props.value] : props.modelValue.filter(v => v !== props.value)) :
      newValue);

    if (props.validateOnInput) {
      formState.validateField(props.name);
    }
  }
}

const localValue = computed<boolean>({
  get: () => {
    if (Array.isArray(props.modelValue)) {
      return props.modelValue.includes(props.value)
    }
    return props.modelValue ?? false
  },
  set: (newValue) => {
    handleModelValueUpdate(newValue)
    updateValue(newValue)
  }
})

// Watch fieldValue changes to update model
watch(() => fieldValue.value, (newVal) => {
  if (typeof newVal === 'boolean') {
    handleModelValueUpdate(newVal)
  }
}, { immediate: true })

const currentErrors = computed(() => {
  if (props.formContext && formState && props.name) {
    return formState.errors.value[props.name] || []
  }
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    if (error.toLowerCase().includes('required')) {
      return t('validation.required')
    }
    return error
  })
})

const isInvalid = computed(() => {
  return props.invalid || shouldShowError.value || (currentErrors.value.length > 0 && isDirty.value)
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  currentErrors.value.forEach((_, index) => ids.push(`${props.id}-error-${index}`))
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return props.showErrors && (!!props.helperText || shouldShowError.value || (currentErrors.value.length > 0 && isDirty.value))
})

// Format error message based on validation type
const getFormattedErrorMessage = computed(() => {
  if (!errorMessage.value && currentErrors.value.length === 0) return ''

  if (props.required && isEmpty(localValue.value)) {
    return `${props.name || 'This field'} is required`
  }

  return errorMessage.value || currentErrors.value[0]
})

const validateCheckbox = async (): Promise<boolean> => {
  if (props.formContext && formState && props.name) {
    return formState.validateField(props.name)
  }

  isValidating.value = true
  try {
    const errors: string[] = []
    const value = localValue.value

    // Built-in required validation for checkbox
    if (props.required) {
      if (Array.isArray(props.modelValue)) {
        // For checkbox groups, check if at least one is selected
        if (props.modelValue.length === 0) {
          errors.push(`${props.name || props.label || 'This field'} is required`)
        }
      } else {
        // For single checkbox, check if it's checked
        if (!value) {
          errors.push(`${props.name || props.label || 'This field'} is required`)
        }
      }
    }

    for (const rule of props.validationRules) {
      let isValid = true
      const message = rule.message

      if (rule.validator) {
        isValid = await rule.validator(value)
      }

      if (!isValid && message) {
        errors.push(message)
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

const onInput = async (event: Event) => {
  emit('input', event)
  isDirty.value = true

  if (props.formContext && props.validateOnInput && formState && props.name) {
    await formState.validateField(props.name)
  }

  if (shouldShowError.value) {
    await veeValidate()
  }
}

const onFocus = (event: Event) => {
  emit('focus', event)
}

const onBlur = async (event: Event) => {
  emit('blur', event)
  isDirty.value = true
  touched.value = true

  if (props.formContext && formState && props.name) {
    formState.setFieldTouched(props.name, true)
    if (props.validateOnBlur) {
      await formState.validateField(props.name)
    }
  }

  await handleValidation()
}

const onChange = async (event: Event) => {
  emit('change', event)
  isDirty.value = true

  if (props.formContext && props.validateOnChange && formState && props.name) {
    await formState.validateField(props.name)
  }

  await veeValidate()
}

// Initialize form context
onMounted(() => {
  if (props.formContext && formState && props.name) {
    formState.addValidation(props.name, props.validationRules)
  }
})

// Watch for external validation triggers
watch(() => props.invalid, (newValue) => {
  if (newValue) {
    isDirty.value = true
  }
})

// Watch for required field changes to trigger validation
watch(() => props.required, (isRequired) => {
  if (isRequired) {
    if (Array.isArray(props.modelValue)) {
      if (props.modelValue.length === 0) {
        isDirty.value = true
        validateCheckbox()
      }
    } else {
      if (!localValue.value) {
        isDirty.value = true
        validateCheckbox()
      }
    }
  }
}, { immediate: true })

defineExpose({
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
  validate: validateCheckbox,
  validationErrors: currentErrors,
  errorMessage,
  isDirty,
  isValidating,
  isValid: computed(() => !isInvalid.value)
})
</script>
